/**
 * 販売者ルート定義（SellerLayout）
 *
 * @file seller-routes.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * 販売者向けページのルーティング設定。
 * SellerLayoutを静的importし、18ルートを管理。
 */
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import { lazyWithI18n } from '@shared/i18n';
import { I18nLoadingScreen } from '../../../frontend-shared/src/contexts/LanguageContext';
import SellerLayout from '@/layouts/SellerLayout';

const lazySellerPage = (
  importFn: () => Promise<{ default: React.ComponentType }>,
  pageNamespaces: string[]
) => {
  return lazyWithI18n(importFn, [
    ...pageNamespaces,
    'layout/SellerLayout',
    'common',
    'errors'
  ]);
};

// ========================================
// 販売者ページ（認証不要）
// ========================================
const SellerLoginPage = lazySellerPage(
  () => import('../pages/SellerLoginPage'),
  ['seller/SellerLoginPage']
);  // S-020: 販売者ログイン

const SellerVerifyEmailChangePage = lazySellerPage(
  () => import('../pages/SellerVerifyEmailChangePage'),
  ['seller/SellerVerifyEmailChangePage']
);  // S-011B: メールアドレス変更確認

// ========================================
// 販売者ページ（認証必須）
// ========================================
const SellerDashboardPage = lazySellerPage(
  () => import('../pages/SellerDashboardPage'),
  ['seller/SellerDashboardPage']
);  // S-001: 販売者ダッシュボード

const SellerProductRegisterPage = lazySellerPage(
  () => import('../pages/SellerProductRegisterPage'),
  ['seller/SellerProductRegisterPage']
);  // S-002: 商品登録（統合版）

const SellerInventoryListPage = lazySellerPage(
  () => import('../pages/SellerInventoryListPage'),
  ['seller/SellerInventoryListPage']
);  // S-003A: 在庫一覧

const SellerInventoryIncomingPage = lazySellerPage(
  () => import('../pages/SellerInventoryIncomingPage'),
  ['seller/SellerInventoryIncomingPage']
);  // S-003B: 入荷予定

const SellerInventoryHistoryPage = lazySellerPage(
  () => import('../pages/SellerInventoryHistoryPage'),
  ['seller/SellerInventoryHistoryPage']
);  // S-003C: 在庫履歴

const SellerOrderPage = lazySellerPage(
  () => import('../pages/SellerOrderPage'),
  ['seller/SellerOrderPage']
);  // S-004: 注文管理

const SellerAnalyticsSalesPage = lazySellerPage(
  () => import('../pages/SellerAnalyticsSalesPage'),
  ['seller/SellerAnalyticsSalesPage']
);  // S-005: 売上トレンド

const SellerAnalyticsProductPage = lazySellerPage(
  () => import('../pages/SellerAnalyticsProductPage'),
  ['seller/SellerAnalyticsProductPage']
);  // S-006: 商品別売上

const SellerAnalyticsBuyerPage = lazySellerPage(
  () => import('../pages/SellerAnalyticsBuyerPage'),
  ['seller/SellerAnalyticsBuyerPage']
);  // S-007: 顧客分析

const SellerPaymentPage = lazySellerPage(
  () => import('../pages/SellerPaymentPage'),
  ['seller/SellerPaymentPage']
);  // S-008: 入金状況

const SellerInformationPage = lazySellerPage(
  () => import('../pages/SellerInformationPage'),
  ['seller/SellerInformationPage']
);  // S-009: 店舗情報設定


const SellerAccountPage = lazySellerPage(
  () => import('../pages/SellerAccountPage'),
  ['seller/SellerAccountPage']
);  // S-011: アカウント設定

const SellerNotificationPage = lazySellerPage(
  () => import('../pages/SellerNotificationPage'),
  ['seller/SellerNotificationPage']
);  // S-012: 通知設定

const SellerCouponPage = lazySellerPage(
  () => import('../pages/SellerCouponPage'),
  ['seller/SellerCouponPage']
);  // S-013: クーポン管理

const SellerChatSupportPage = lazySellerPage(
  () => import('../pages/SellerChatSupportPage'),
  ['seller/SellerChatSupportPage']
);  // S-014: チャットサポート管理

const SellerHelpPage = lazySellerPage(
  () => import('../pages/SellerHelpPage'),
  ['seller/SellerHelpPage']
);  // S-015: FAQ・ヘルプページ（販売者向け）

const SellerRoutes: React.FC = () => {
  return (
    <Routes>
      {/* ルートアクセス時に /seller へリダイレクト */}
      <Route path="/" element={<Navigate to="/seller" replace />} />

      {/* S-020: 販売者ログイン */}
      <Route path="login" element={
        <Suspense fallback={<I18nLoadingScreen />}>
          <SellerLoginPage />
        </Suspense>
      } />

      {/* S-011B: メールアドレス変更確認 */}
      <Route path="verify-email-change" element={
        <Suspense fallback={<I18nLoadingScreen />}>
          <SellerVerifyEmailChangePage />
        </Suspense>
      } />

      <Route path="/seller" element={
        <ProtectedRoute sellerOnly>
          <SellerLayout />
        </ProtectedRoute>
      }>
        {/* S-001: 販売者ダッシュボード */}
        <Route index element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerDashboardPage />
          </Suspense>
        } />

        {/* S-002: 商品登録（統合版） */}
        <Route path="products/new" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerProductRegisterPage />
          </Suspense>
        } />

        {/* S-003: 在庫管理（旧URL → /list リダイレクト） */}
        <Route path="inventory" element={<Navigate to="/seller/inventory/list" replace />} />

        {/* S-003A: 在庫一覧 */}
        <Route path="inventory/list" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerInventoryListPage />
          </Suspense>
        } />

        {/* S-003B: 入荷予定 */}
        <Route path="inventory/incoming" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerInventoryIncomingPage />
          </Suspense>
        } />

        {/* S-003C: 在庫履歴 */}
        <Route path="inventory/history" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerInventoryHistoryPage />
          </Suspense>
        } />

        {/* S-004: 注文管理 */}
        <Route path="orders" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerOrderPage />
          </Suspense>
        } />

        {/* S-005: 売上トレンド */}
        <Route path="analytics/sales" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerAnalyticsSalesPage />
          </Suspense>
        } />

        {/* S-006: 商品別売上 */}
        <Route path="analytics/products" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerAnalyticsProductPage />
          </Suspense>
        } />

        {/* S-007: 顧客分析 */}
        <Route path="analytics/buyers" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerAnalyticsBuyerPage />
          </Suspense>
        } />

        {/* S-008: 入金状況 */}
        <Route path="payments" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerPaymentPage />
          </Suspense>
        } />

        {/* S-009: 店舗情報設定 */}
        <Route path="info" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerInformationPage />
          </Suspense>
        } />

        {/* S-011: アカウント設定 */}
        <Route path="account" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerAccountPage />
          </Suspense>
        } />

        {/* S-012: 通知設定 */}
        <Route path="notifications" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerNotificationPage />
          </Suspense>
        } />

        {/* S-013: クーポン管理 */}
        <Route path="coupons" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerCouponPage />
          </Suspense>
        } />

        {/* S-014: チャットサポート管理 */}
        <Route path="chat-support" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerChatSupportPage />
          </Suspense>
        } />

        {/* S-015: FAQ・ヘルプページ（販売者向け） */}
        <Route path="help" element={
          <Suspense fallback={<I18nLoadingScreen />}>
            <SellerHelpPage />
          </Suspense>
        } />
      </Route>
    </Routes>
  );
};

export default SellerRoutes;
